import React, { useState } from 'react';
import {
  Box,
  Typography,
  Switch,
  Collapse,
  alpha,
  useTheme
} from '@mui/material';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';

const ProformaInvoicePermissions = ({ 
  permissions, 
  roleForm, 
  setRoleForm 
}) => {
  const theme = useTheme();
  const [expandedSections, setExpandedSections] = useState({
    special_permissions: false,
    proforma_invoices_page: false
  });
  
  // Special Permissions Section
  const renderSpecialPermissions = () => {
    const isExpanded = expandedSections.special_permissions !== false;
    const createPermission = permissions.find(p => p.name === 'sales.purchase_orders.create');
    const deletePermission = permissions.find(p => p.name === 'sales.purchase_orders.delete');
    
    if (!createPermission && !deletePermission) return null;

    return (
      <Box 
        sx={{ 
          mb: 2,
          border: `1px solid ${alpha(theme.palette.grey[400], 0.5)}`,
          borderRadius: 2,
          overflow: 'hidden',
          background: 'white'
        }}
      >
        <Box
          onClick={() => setExpandedSections(prev => ({ ...prev, special_permissions: !isExpanded }))}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            cursor: 'pointer',
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.03)}, ${alpha(theme.palette.primary.main, 0.01)})`,
            borderBottom: isExpanded ? `1px solid ${alpha(theme.palette.grey[400], 0.3)}` : 'none',
            '&:hover': {
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.06)}, ${alpha(theme.palette.primary.main, 0.03)})`
            }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <AddCircleIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.primary' }}>
              Special Permissions
            </Typography>
          </Box>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {isExpanded ? '▼' : '▶'}
          </Typography>
        </Box>

        <Collapse in={isExpanded}>
          <Box sx={{ p: 2, border: `1px solid ${alpha(theme.palette.grey[400], 0.5)}`, borderTop: 'none', borderRadius: '0 0 8px 8px', background: 'white' }}>
            {createPermission && (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                py: 1,
                borderBottom: deletePermission ? `1px solid ${alpha(theme.palette.grey[300], 0.5)}` : 'none'
              }}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Create Proforma Invoices
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Can create new proforma invoices
                  </Typography>
                </Box>
                <Switch
                  checked={roleForm.permissions[createPermission.id]?.write || false}
                  onChange={(e) => {
                    const newPermissions = { ...roleForm.permissions };
                    if (!newPermissions[createPermission.id]) {
                      newPermissions[createPermission.id] = {};
                    }
                    newPermissions[createPermission.id].write = e.target.checked;
                    setRoleForm({ ...roleForm, permissions: newPermissions });
                  }}
                  size="small"
                />
              </Box>
            )}
            {deletePermission && (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                py: 1
              }}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Delete Proforma Invoices
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Can delete proforma invoices
                  </Typography>
                </Box>
                <Switch
                  checked={roleForm.permissions[deletePermission.id]?.delete || false}
                  onChange={(e) => {
                    const newPermissions = { ...roleForm.permissions };
                    if (!newPermissions[deletePermission.id]) {
                      newPermissions[deletePermission.id] = {};
                    }
                    newPermissions[deletePermission.id].delete = e.target.checked;
                    setRoleForm({ ...roleForm, permissions: newPermissions });
                  }}
                  size="small"
                />
              </Box>
            )}
          </Box>
        </Collapse>
      </Box>
    );
  };

  // Purchase Order Page Section
  const renderPurchaseOrderPage = () => {
    const isExpanded = expandedSections.proforma_invoices_page !== false;
    const pagePermission = permissions.find(p => p.name === 'sales.purchase_orders.page.view');
    
    if (!pagePermission) return null;

    return (
      <Box 
        sx={{ 
          mb: 2,
          border: `1px solid ${alpha(theme.palette.grey[400], 0.5)}`,
          borderRadius: 2,
          overflow: 'hidden',
          background: 'white'
        }}
      >
        <Box
          onClick={() => setExpandedSections(prev => ({ ...prev, proforma_invoices_page: !isExpanded }))}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            cursor: 'pointer',
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.03)}, ${alpha(theme.palette.primary.main, 0.01)})`,
            borderBottom: isExpanded ? `1px solid ${alpha(theme.palette.grey[400], 0.3)}` : 'none',
            '&:hover': {
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.06)}, ${alpha(theme.palette.primary.main, 0.03)})`
            }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <RequestQuoteIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.primary' }}>
              Proforma Invoices Page
            </Typography>
          </Box>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {isExpanded ? '▼' : '▶'}
          </Typography>
        </Box>

        <Collapse in={isExpanded}>
          <Box sx={{ p: 2 }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              py: 1
            }}>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  View
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Can view proforma invoices page
                </Typography>
              </Box>
              <Switch
                checked={roleForm.permissions[pagePermission.id]?.read || false}
                onChange={(e) => {
                  const newPermissions = { ...roleForm.permissions };
                  if (!newPermissions[pagePermission.id]) {
                    newPermissions[pagePermission.id] = {};
                  }
                  newPermissions[pagePermission.id].read = e.target.checked;
                  setRoleForm({ ...roleForm, permissions: newPermissions });
                }}
                size="small"
              />
            </Box>
          </Box>
        </Collapse>
      </Box>
    );
  };

  return (
    <Box>
      {renderSpecialPermissions()}
      {renderPurchaseOrderPage()}
    </Box>
  );
};

export default ProformaInvoicePermissions;
